"use client"

import { useState, useEffect } from "react"
import { enqueueSnackbar } from "notistack"
import { useRouter } from "next/navigation"
import { captureException } from "@sentry/nextjs"

import { initiateLogin } from "@/actions/login"
import AuthForm from "@/components/auth_form"
import Helmet from "@/atoms/helmet"
import LoginFooter from "@/molecules/login_footer"
import { fixPhone, isPhoneValid } from "@/utils/validator"
import useTranslation from "@/hooks/use_translation"
import { useReduxState } from "@/hooks/use_redux_state"
import { buildContinueWithFromSearch } from "@/utils/url"
import withAnonymousRoute from "@/molecules/anonymous_route"
import { useLoggedOutAppSelector } from "@/lib/hooks"

type Challenge = { identifier: { phone: string } }

const Login = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const [inAction, setInAction] = useState(false)
  const [, setAuthPayload] = useReduxState({ key: "authPayload" })
  const defaultPhoneLocale = useLoggedOutAppSelector((state) => state.configuration.defaultPhoneLocale)

  useEffect(() => {
    setAuthPayload(null)
  }, [setAuthPayload])

  const tfaCallback = (challenge: Challenge) => {
    setInAction(false)
    gtag("event", "login_to_tfa_success")
    setAuthPayload({ identifier: challenge.identifier, signupPayload: null })
    const nextPage = `/tfa/?${buildContinueWithFromSearch()}`
    router.push(nextPage)
  }

  const badCallback = (status: number | string) => {
    setInAction(false)
    gtag("event", "login_to_tfa_failure")
    if (status === 404) {
      enqueueSnackbar(t("phoneNotExists"), { variant: "error", autoHideDuration: 5000 })
    } else {
      console.error(`Login failed with status ${status}`)
      captureException(new Error(`Login failed with status ${status}`))
      enqueueSnackbar(t("unexpectedError"), { variant: "error", autoHideDuration: 5000 })
    }
  }

  const onSubmit = (phone: string) => {
    const fixed = fixPhone(phone, defaultPhoneLocale)
    if (!fixed) return
    setInAction(true)
    initiateLogin(fixed, false, tfaCallback, badCallback)
  }

  const validate = (phone: string) => {
    if (!phone) {
      return t("enterPhoneNumber")
    }
    if (!isPhoneValid(phone, defaultPhoneLocale)) {
      return t("phoneIsNotValid")
    }
    return undefined
  }

  return (
    <>
      <Helmet>
        <title>{t("login")}</title>
      </Helmet>
      <AuthForm
        title={t("logToYourAccount")}
        message={t("generalLoginText")}
        fieldType="phone"
        fieldLabel={t("phoneNumber")}
        buttonText={t("login")}
        inAction={inAction}
        validate={validate}
        onSubmit={onSubmit}
        footer={<LoginFooter />}
      />
    </>
  )
}

export default withAnonymousRoute(Login)
